import { Box } from 'mds-web-ui'
import { useMotionValueEvent, useSpring, useTransform } from 'framer-motion'
import { GatsbyImage } from 'gatsby-plugin-image'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { border } from 'styled-system'

export const ZoomableImage = ({ image, alt, zoomFactor = 2, ...props }) => {
  const imageRef = useRef()
  const [zoomed, setZoomed] = useState(false)

  const scaleFactor = useSpring(0, { stiffness: 1000, damping: 100 })
  const scale = useTransform(scaleFactor, [0, 1], [1, zoomFactor])
  const x = useSpring(0, { stiffness: 1000, damping: 100 })
  const y = useSpring(0, { stiffness: 1000, damping: 100 })

  useMotionValueEvent(scale, 'change', v => {
    const img = imageRef.current
    if (!img) {
      return
    }

    img.style.setProperty('--scale', v)
  })

  useMotionValueEvent(x, 'change', v => {
    const img = imageRef.current
    if (!img) {
      return
    }

    img.style.setProperty('--x', `${v}%`)
  })

  useMotionValueEvent(y, 'change', v => {
    const img = imageRef.current
    if (!img) {
      return
    }

    img.style.setProperty('--y', `${v}%`)
  })

  useEffect(() => {
    const img = imageRef.current
    if (!img) {
      return
    }

    img.style.cursor = zoomed ? 'zoom-out' : 'zoom-in'
    scaleFactor.set(zoomed ? 1 : 0)
    if (!zoomed) {
      x.set(0)
      y.set(0)
    }
  }, [scaleFactor, x, y, zoomed])

  useEffect(() => {
    const img = imageRef.current
    if (!img) {
      return
    }

    const onMouseMove = e => {
      if (!zoomed) {
        return
      }

      const { left, top, width, height } = img.getBoundingClientRect()
      // Map the coordinates to -25 to 25 so we can translate the entire image with the mouse
      // Also, invert the positions here so the view of the image more closely tracks the image
      const xPos = (((e.clientX - left) / width) * 2 - 1) * 25 * -1
      const yPos = (((e.clientY - top) / height) * 2 - 1) * 25 * -1

      x.set(xPos)
      y.set(yPos)
    }

    img.addEventListener('mousemove', onMouseMove)

    return () => {
      img.removeEventListener('mousemove', onMouseMove)
    }
  }, [image, x, y, zoomed])

  return (
    <BorderBox
      width="100%"
      borderRadius={[0, 12]}
      px={[0, 3, 4]}
      {...props}
      ref={imageRef}
      onClick={() => setZoomed(z => !z)}
    >
      <GatsbyImage image={image} alt={alt} />

      <ZoomLabel>{zoomed ? `${zoomFactor}00%` : '100%'}</ZoomLabel>
    </BorderBox>
  )
}

const BorderBox = styled(Box)(
  {
    '--scale': 1,
    '--x': '0%',
    '--y': '0%',
    overflow: 'hidden',
    position: 'relative',
    '*': {
      borderRadius: 'inherit'
    },
    'picture img': {
      transform: 'scale(var(--scale)) translate(var(--x), var(--y)) !important'
    }
  },
  border
)

const ZoomLabel = styled('div')({
  position: 'absolute',
  bottom: 20,
  right: 54,
  borderRadius: 8,
  padding: '4px 8px',
  background: 'none',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 240ms ease-out',
  zIndex: 999,
  cursor: 'pointer',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    borderRadius: 'inherit',
    background: '#fff',
    opacity: 0.5,
    zIndex: -1,
    transition: 'all 120ms ease-out',
    transform: 'scale(1)'
  },

  '&:hover': {
    '&::before': {
      opacity: 1,
      transform: 'scale(1.05)'
    }
  }
})
